import { useEffect, useState } from 'react';
import { ChevronRight } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '@/stores/authStore';
import { useChildCount } from '@/hooks/useChildCount';
import { useEntitlement } from '@/hooks/useEntitlement';
import { useWorldSelectionSheet } from '@/components/worlds/useWorldSelectionSheet';
import { useStoryCount } from '@/hooks/useStoryCount';
import { useSubscriptionState } from '@/hooks/useSubscriptionState';
import { fetchGiftBalance } from '@/features/gifts/api';
import { ContentContainer } from '@/components/shell/ContentContainer';
import { PlanStatusCard } from '@/components/patterns/PlanStatusCard';
import { SectionHeading } from '@/components/patterns/SectionHeading';
import {
  derivePlanStatusProps,
  deriveLifecyclePlanStatusProps,
} from '@/features/subscription/derive-plan-status-props';
import {
  createPortalSession,
  reactivateSubscription,
  discardScheduledChange,
} from '@/features/subscription/api';
import { ApiError, NetworkError } from '@/lib/api-client';
import { STRINGS } from '@/strings/strings';
import { LEGAL_URLS } from '@/lib/config';
import { cn } from '@/lib/utils';

/**
 * Account-Übersicht (A-01) — `/account`.
 *
 * Sönkes mockup `dreamable_ch2_account_overview_and_subpages.html`
 * (lines 37-60). Layout sequence: greeting → PlanStatusCard
 * (Credits-states OR lifecycle-states via priority switch) → "Mein
 * Konto" link list → "Rechtliches" link list → "Abmelden" outlined
 * button.
 *
 * Lifecycle-priority switch: when `useSubscriptionState()` returns a
 * lifecycle phase (Past-Due / Pending-Cancel / Pending-Plan-Change),
 * `deriveLifecyclePlanStatusProps` returns non-null and feeds the
 * PlanStatusCard with warning/error tone props. Otherwise we fall
 * through to `derivePlanStatusProps(entitlement)` for credits-states.
 *
 * Sönke design-system rollout: `min-h-screen bg-dream-white` outer
 * shell (matches StartRoute pattern + tracker #28); ContentContainer
 * for desktop max-width; inline `desktop:max-w-[768px] mx-auto` for
 * the inner Sönke `desktop-max-container` cap.
 *
 * Stub destinations for sub-pages (Batch 9B/9C/legal) are wired here
 * already even though those routes don't exist yet — clicking them
 * routes to React Router no-match. Acceptable scope-bound state for
 * Batch 9A; later batches wire each target.
 */
export function AccountRoute() {
  const navigate = useNavigate();
  const firstName = useAuthStore((s) => s.user?.firstName ?? '');
  const logout = useAuthStore((s) => s.logout);
  const { entitlement } = useEntitlement({ refetchOnMount: true });
  const { state: subscriptionState, refetch: refetchSubscriptionState } = useSubscriptionState({
    refetchOnMount: true,
  });
  // Batch 8 Commit 5 — count badges on Bibliothek + Kinderprofile
  // (Briefing line 298-299). Caches invalidated by ChildrenRoute /
  // ChildProfileRoute (children) + LibraryRoute delete (stories).
  const { count: storyCount } = useStoryCount();
  const { count: childCount } = useChildCount();

  const [actionError, setActionError] = useState<string | null>(null);
  // P14 Block 4 (2026-09-23) — gift-balance line below PlanStatusCard.
  // Silent-fail: if the fetch errors (feature flag off, envelope
  // malformed) the line simply doesn't render. Ledger only reveals
  // itself when balanceCents > 0 — zero-balance users see nothing.
  const [giftBalanceCents, setGiftBalanceCents] = useState<number | null>(null);
  useEffect(() => {
    let cancelled = false;
    fetchGiftBalance()
      .then((r) => {
        if (!cancelled) setGiftBalanceCents(r.balanceCents);
      })
      .catch(() => {
        if (!cancelled) setGiftBalanceCents(null);
      });
    return () => {
      cancelled = true;
    };
  }, []);
  // Bug PWA-A Bug-B: reentry guard prevents the reactivate double-fire that
  // produced the false-error banner. The reactivate CTA here lives in the
  // shared PlanStatusCard, so the guard is handler-level (not a button
  // disabled prop, which would expand scope to PlanStatusCard).
  const [isReactivating, setIsReactivating] = useState(false);
  // D1: reentry guard for the in-app "Plan-Wechsel rückgängig machen" discard.
  const [isDiscarding, setIsDiscarding] = useState(false);

  // ── Lifecycle action handlers (mirror SubscriptionRoute Commit 3 below)

  const handleUpdatePaymentMethod = async () => {
    setActionError(null);
    try {
      const { url } = await createPortalSession('payment_method_update');
      window.location.assign(url);
    } catch (err) {
      console.error(err);
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.portalSessionFailed);
      }
    }
  };

  const handleReactivate = async () => {
    if (isReactivating) return;
    setActionError(null);
    setIsReactivating(true);
    try {
      await reactivateSubscription();
      // Webhook syncs DB asynchronously. Refetch at 500ms, then again at
      // 2000ms as a safety net for slow webhooks (Bug PWA-A polish — fixes
      // the refresh-race where the single 500ms refetch fired before the DB
      // was updated, so the UI only refreshed on hard-reload).
      window.setTimeout(() => {
        void refetchSubscriptionState();
      }, 500);
      window.setTimeout(() => {
        void refetchSubscriptionState();
      }, 2000);
    } catch (err) {
      console.error(err);
      // Bug PWA-A Bug-B: a 400 subscription_not_pending_cancel means the
      // reactivation already took effect server-side (double-fire race or a
      // stale CTA rendered from cached state). Treat as success — refetch the
      // synced state, suppress the red banner.
      if (
        err instanceof ApiError &&
        err.httpStatus === 400 &&
        err.errorCode === 'subscription_not_pending_cancel'
      ) {
        // Same double-refetch race fix as the success path above.
        window.setTimeout(() => {
          void refetchSubscriptionState();
        }, 500);
        window.setTimeout(() => {
          void refetchSubscriptionState();
        }, 2000);
        return;
      }
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.reactivateFailed);
      }
    } finally {
      setIsReactivating(false);
    }
  };

  // D1: in-app discard of a scheduled plan change. Mirrors handleReactivate —
  // reentry guard, double-refetch (500ms + 2000ms), and success-equivalent
  // handling of the "already gone" backend codes. Replaces the prior
  // portal-redirect handler (which had no other consumer on this route).
  const handleDiscardScheduledChange = async () => {
    if (isDiscarding) return;
    setActionError(null);
    setIsDiscarding(true);
    try {
      await discardScheduledChange();
      window.setTimeout(() => {
        void refetchSubscriptionState();
      }, 500);
      window.setTimeout(() => {
        void refetchSubscriptionState();
      }, 2000);
    } catch (err) {
      console.error(err);
      // The scheduled change is already gone (double-fire race or a stale CTA):
      // 404 subscription_no_scheduled_change / 409 subscription_schedule_
      // already_terminated → treat as success, refetch, suppress the banner.
      if (
        err instanceof ApiError &&
        ((err.httpStatus === 404 && err.errorCode === 'subscription_no_scheduled_change') ||
          (err.httpStatus === 409 && err.errorCode === 'subscription_schedule_already_terminated'))
      ) {
        window.setTimeout(() => {
          void refetchSubscriptionState();
        }, 500);
        window.setTimeout(() => {
          void refetchSubscriptionState();
        }, 2000);
        return;
      }
      if (err instanceof ApiError || err instanceof NetworkError) {
        setActionError(STRINGS.account.errors.discardFailed);
      }
    } finally {
      setIsDiscarding(false);
    }
  };

  const lifecycleProps = deriveLifecyclePlanStatusProps(subscriptionState, {
    onUpdatePaymentMethod: () => void handleUpdatePaymentMethod(),
    onReactivate: () => void handleReactivate(),
    // D1: the "Plan-Wechsel rückgängig machen" CTA now triggers the in-app
    // discard endpoint (was a Stripe-Portal redirect).
    onCancelPendingPlanChange: () => void handleDiscardScheduledChange(),
  });
  // Batch 10 Sub-phase D — A-01 Plan-Status-Card "Geschichte erstellen"
  // CTA opens the global WorldSelectionSheet instead of navigating to
  // the (deleted) bare /builder route.
  // Sub-phase F — wire useSubscriptionState fields so Active-Sub users
  // see the proper "{tierLabel}-Abo" + "{used} von {total} diesen Monat"
  // framing per Decision #16. Closes the Sub-phase B fallback to
  // "Abo" + pack-style count.
  const { openSheet } = useWorldSelectionSheet();
  const planProps =
    lifecycleProps ??
    derivePlanStatusProps(entitlement, navigate, {
      chooseplanTarget: '/account/subscription',
      onCreateStoryClick: openSheet,
      subscriptionTierLabel: subscriptionState?.tierLabel ?? null,
      subscriptionStoriesPerMonth: subscriptionState?.storiesPerMonth ?? null,
    });

  const handleLogout = async () => {
    await logout();
    navigate('/auth/login');
  };

  const greetingText = firstName
    ? STRINGS.account.greeting.replace('{firstName}', firstName)
    : STRINGS.account.greetingFallback;

  return (
    <div className="min-h-screen bg-dream-white">
      <ContentContainer>
        <div className="flex flex-col gap-5 py-5 md:gap-7 md:pt-10 md:pb-14 desktop:mx-auto desktop:max-w-[768px]">
          <p
            className="text-base font-medium leading-4 text-dream-navy"
            data-testid="account-greeting"
          >
            {greetingText}
          </p>

          <PlanStatusCard {...planProps} testId="account-plan-status-card" />

          {giftBalanceCents !== null && giftBalanceCents > 0 && (
            <p className="text-xs text-dream-navy/60" data-testid="account-gift-balance-line">
              {STRINGS.account.balance.line.replace(
                '{amount}',
                new Intl.NumberFormat('de-DE', {
                  style: 'currency',
                  currency: 'EUR',
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                }).format(giftBalanceCents / 100),
              )}
            </p>
          )}

          {isDiscarding && (
            <p className="text-xs text-dream-navy/60" data-testid="account-discarding-indicator">
              {STRINGS.account.subscription.discarding}
            </p>
          )}

          {isReactivating && (
            <p className="text-xs text-dream-navy/60" data-testid="account-reactivating-indicator">
              {STRINGS.account.subscription.reactivating}
            </p>
          )}

          {actionError && (
            <p role="alert" className="text-xs text-error">
              {actionError}
            </p>
          )}

          <SectionHeading
            heading={STRINGS.account.sections.meinKonto}
            id="account-mein-konto-heading"
          >
            <ul className="flex w-full flex-col gap-2">
              <AccountLinkRow label={STRINGS.account.links.meinProfil} to="/account/profile" />
              <AccountLinkRow label={STRINGS.account.links.meinAbo} to="/account/subscription" />
              <AccountLinkRow
                label={STRINGS.account.links.kinderprofile}
                to="/account/children"
                count={childCount}
              />
              <AccountLinkRow
                label={STRINGS.account.links.bibliothek}
                to="/library"
                count={storyCount}
              />
              <AccountLinkRow
                label={STRINGS.account.links.benachrichtigungen}
                to="/account/notifications"
              />
              <AccountLinkRow label={STRINGS.account.links.passwort} to="/account/password" />
              <AccountLinkRow label={STRINGS.account.links.hilfe} href={LEGAL_URLS.faq} />
            </ul>
          </SectionHeading>

          <SectionHeading
            heading={STRINGS.account.sections.rechtliches}
            id="account-rechtliches-heading"
            tone="secondary"
          >
            <ul className="flex w-full flex-col gap-2">
              <AccountLinkRow
                label={STRINGS.account.links.datenexport}
                to="/account/export"
                tone="secondary"
              />
              <AccountLinkRow
                label={STRINGS.account.links.kontoLoeschen}
                to="/account/delete"
                tone="secondary"
              />
              <AccountLinkRow
                label={STRINGS.account.links.terms}
                href={LEGAL_URLS.terms}
                tone="secondary"
              />
              <AccountLinkRow
                label={STRINGS.account.links.privacy}
                href={LEGAL_URLS.privacy}
                tone="secondary"
              />
              <AccountLinkRow
                label={STRINGS.account.links.impressum}
                href={LEGAL_URLS.impressum}
                tone="secondary"
              />
            </ul>
          </SectionHeading>

          <button
            type="button"
            onClick={() => void handleLogout()}
            className={cn(
              'mt-2 h-11 w-full rounded-lg border border-dream-navy/40 bg-transparent text-sm font-medium text-dream-navy/70 transition-colors hover:bg-dream-navy/5',
            )}
            data-testid="account-logout-button"
          >
            {STRINGS.account.logout}
          </button>
        </div>
      </ContentContainer>
    </div>
  );
}

// ── Route-private AccountLinkRow ──────────────────────────────

interface AccountLinkRowProps {
  label: string;
  /** Internal route (rendered as a navigate() button). Use exactly one of `to` / `href`. */
  to?: string;
  /**
   * External URL (rendered as an <a target="_blank" rel="noopener noreferrer">),
   * same styling as the `to` variant. Used by the "Rechtliches" legal links
   * (NB / Datenschutz / Impressum) which live on the Webflow site.
   */
  href?: string;
  tone?: 'primary' | 'secondary';
  /**
   * Optional count rendered as a `({count})` suffix after the label
   * (Batch 8 Commit 5). Used today by Bibliothek + Kinderprofile rows
   * per Briefing line 298-299. `undefined` (default) renders the label
   * alone, identical to Batch 9A behaviour.
   */
  count?: number;
}

/**
 * Single tap-row inside the "Mein Konto" / "Rechtliches" lists.
 * Currently route-private to AccountRoute. Promote to
 * `pwa/src/components/patterns/` when a 2nd consumer surfaces.
 *
 * Batch 8 Commit 5: optional `count` prop renders as a `({count})`
 * suffix after the label (Briefing-spec'd "Kinderprofile ({count})" /
 * "Bibliothek ({count})" pattern).
 */
function AccountLinkRow({ label, to, href, tone = 'primary', count }: AccountLinkRowProps) {
  const navigate = useNavigate();
  const toneClass = tone === 'secondary' ? 'text-dream-navy/60' : 'text-dream-navy';
  const rowClass = cn(
    'flex w-full items-center justify-between rounded-lg border border-dream-navy/10 bg-dream-white px-4 py-3 text-left text-sm font-medium transition-colors hover:bg-dream-navy/5',
    toneClass,
  );
  const testId = `account-link-${slugify(label)}`;
  const inner = (
    <>
      <span>
        {label}
        {count !== undefined && ` (${count})`}
      </span>
      <ChevronRight className="h-4 w-4 shrink-0 opacity-40" strokeWidth={2} />
    </>
  );

  return (
    <li className="w-full">
      {href ? (
        <a
          href={href}
          target="_blank"
          rel="noopener noreferrer"
          className={rowClass}
          data-testid={testId}
        >
          {inner}
        </a>
      ) : (
        <button
          type="button"
          onClick={() => to && navigate(to)}
          className={rowClass}
          data-testid={testId}
        >
          {inner}
        </button>
      )}
    </li>
  );
}

function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
